import React, {useEffect, useMemo, useState} from "react"
import classNames from "classnames"
import {DraggableSyntheticListeners, UniqueIdentifier} from "@dnd-kit/core"
import type {Transform} from "@dnd-kit/utilities"
import styles from "./Item.module.scss"
import {GoGrabber} from "react-icons/go"
import {FormInput} from "../../../components/Form/FormInput"
import {MinusCircleOutlined, MinusOutlined, PlusOutlined} from "@ant-design/icons"
import {FormTextarea} from "../../../components/Form/FormTextarea"
import {Tooltip} from "antd"
import {FormSelect} from "../../../components/Form/FormSelect"

export interface Props
{
    dragOverlay?: boolean;
    color?: string;
    disabled?: boolean;
    dragging?: boolean;
    handle?: boolean;
    handleProps?: any;
    height?: number;
    fadeIn?: boolean;
    transform?: Transform | null;
    listeners?: DraggableSyntheticListeners;
    sorting?: boolean;
    style?: React.CSSProperties;
    transition?: string | null;
    wrapperStyle?: React.CSSProperties;
    value: React.ReactNode;

    id: UniqueIdentifier;
    containerId: UniqueIdentifier;
    values: any
    errors: any
    touched: any
    handleBlur: any
    handleChange: any
    setFieldValue: any
    onRemove: any
}

export const Item = React.memo(React.forwardRef<HTMLLIElement, Props>(
        (
            {
                color,
                dragOverlay,
                dragging,
                disabled,
                fadeIn,
                handle,
                handleProps,
                height,
                listeners,
                sorting,
                style,
                transition,
                transform,
                value,
                wrapperStyle,
                id,
                containerId,
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                setFieldValue,
                onRemove,
                ...props
            },
            ref,
        ) =>
        {
            const [countOption, setCountOption] = useState(0)

            useEffect(() =>
            {
                if (!dragOverlay)
                {
                    return
                }

                document.body.style.cursor = "grabbing"

                return () =>
                {
                    document.body.style.cursor = ""
                }
            }, [dragOverlay])

            const groupKeyId = useMemo(() => containerId?.toString().replace("group", ""), [containerId])
            const attrKeyId = useMemo(() => id?.toString().replace("attr", ""), [id])

            useEffect(() =>
            {
                if (!values.options) return

                let count = 0

                const optValueKeys = Object.keys(values.options)

                for (const optValueKey of optValueKeys)
                {
                    const optNumber = parseInt(optValueKey)
                    if (optNumber > count) count = optNumber
                }

                setCountOption(count + 1)
            }, [values?.options, attrKeyId])

            const attrOptions = useMemo(() => values?.options ? Object.keys(values.options) : [], [values?.options])

            return (
                <li
                    className={classNames(
                        styles.Wrapper,
                        fadeIn && styles.fadeIn,
                        sorting && styles.sorting,
                        dragOverlay && styles.dragOverlay,
                    )}
                    style={
                        {
                            ...wrapperStyle,
                            transition: [transition, wrapperStyle?.transition]
                                .filter(Boolean)
                                .join(", "),
                            "--translate-x": transform
                                ? `${Math.round(transform.x)}px`
                                : undefined,
                            "--translate-y": transform
                                ? `${Math.round(transform.y)}px`
                                : undefined,
                            "--scale-x": transform?.scaleX
                                ? `${transform.scaleX}`
                                : undefined,
                            "--scale-y": transform?.scaleY
                                ? `${transform.scaleY}`
                                : undefined,
                            "--color": color,
                        } as React.CSSProperties
                    }
                    ref={ref}
                >
                    <div
                        className={classNames(
                            styles.Item,
                            dragging && styles.dragging,
                            handle && styles.withHandle,
                            dragOverlay && styles.dragOverlay,
                            disabled && styles.disabled,
                            color && styles.color,
                        )}
                        style={{
                            ...style,
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 10,
                            outline: "none",
                            cursor: "unset",
                        }}
                        data-cypress="draggable-item"
                        {...props}
                        tabIndex={!handle ? 0 : undefined}
                    >
                        <GoGrabber size={20}
                                   {...(!handle ? listeners : undefined)}
                                   style={{minWidth: 20, cursor: "grab"}}
                        />

                        <div style={{display: "flex", flexDirection: "column", gap: 10, flex: 1}}>
                            <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: 10}}>
                                <FormInput value={values.key}
                                           placeholder="Атрибут"
                                           name={`${groupKeyId}.attributes.${attrKeyId}.key`}
                                           onBlur={handleBlur}
                                           onChange={(e: any) => setFieldValue(`${groupKeyId}.attributes.${attrKeyId}.key`, e.target.value.replace(" ", "_").trim())}
                                           isInvalid={errors.key}
                                           showErrorMessage={false}
                                           containerStyle={{width: "100%", marginBottom: 0}}
                                           allowClear
                                />

                                <FormTextarea value={values.value}
                                              placeholder="Описание"
                                              name={`${groupKeyId}.attributes.${attrKeyId}.value`}
                                              onBlur={handleBlur}
                                              onChange={handleChange}
                                              isInvalid={errors.value}
                                              showErrorMessage={false}
                                              containerStyle={{width: "100%", marginBottom: 0}}
                                              allowClear
                                              autoSize={{minRows: 1}}
                                />

                                {
                                    attrOptions?.length > 0
                                        ? <FormSelect placeholder="По умолчанию"
                                                      value={values.defaultValue}
                                                      onChange={(value) => setFieldValue(`${groupKeyId}.attributes.${attrKeyId}.defaultValue`, value)}
                                                      options={attrOptions?.map(optKey => {
                                                          const key = values.options[optKey]
                                                          return ({value: key.value, label: key.label})
                                                      })}
                                                      className="form-select"
                                                      isInvalid={errors.defaultValue}
                                                      containerStyle={{width: "100%", marginBottom: 0}}
                                                      allowClear
                                        />
                                        : <FormInput value={values.defaultValue}
                                                     placeholder="По умолчанию"
                                                     type="number"
                                                     name={`${groupKeyId}.attributes.${attrKeyId}.defaultValue`}
                                                     onBlur={handleBlur}
                                                     onChange={handleChange}
                                                     isInvalid={errors.defaultValue}
                                                     showErrorMessage={false}
                                                     containerStyle={{width: "100%", marginBottom: 0}}
                                                     allowClear
                                        />
                                }
                            </div>

                            <div style={{display: "flex", flexDirection: "column", gap: 10}}>
                                {attrOptions?.map(optKey =>
                                {
                                    const value = values?.options?.[optKey] ?? {}
                                    const touch = touched?.options?.[optKey] ?? {}
                                    const error = errors?.options?.[optKey] ?? {}
                                    return (
                                        <div key={optKey} style={{display: "flex", flexDirection: "row", gap: 10}}>
                                            <FormInput value={value.value}
                                                       placeholder="Значение"
                                                       type="number"
                                                       name={`${groupKeyId}.attributes.${attrKeyId}.options.${optKey}.value`}
                                                       onBlur={handleBlur}
                                                       onChange={handleChange}
                                                       isInvalid={error.value}
                                                       showErrorMessage={false}
                                                       containerStyle={{width: "100%", marginBottom: 0}}
                                                       allowClear
                                            />

                                            <FormInput value={value.label}
                                                       placeholder="Расшифровка"
                                                       name={`${groupKeyId}.attributes.${attrKeyId}.options.${optKey}.label`}
                                                       onBlur={handleBlur}
                                                       onChange={handleChange}
                                                       isInvalid={error.label}
                                                       showErrorMessage={false}
                                                       containerStyle={{width: "100%", marginBottom: 0}}
                                                       allowClear
                                            />

                                            <Tooltip title="Удалить расшифровку значений атрибута">
                                                <MinusOutlined className="dynamic-delete-button"
                                                               style={{margin: 0}}
                                                               onClick={() =>
                                                               {
                                                                   setFieldValue(`${groupKeyId}.attributes.${attrKeyId}.options.${optKey}`, undefined)
                                                               }}
                                                />
                                            </Tooltip>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        <div style={{position: "relative", height: "100%", display: "flex", alignItems: "center"}}>
                            {onRemove ? <Tooltip title="Удалить атрибут">
                                <MinusCircleOutlined className="dynamic-delete-button"
                                                     style={{margin: "auto"}}
                                                     onClick={onRemove}
                                />
                            </Tooltip> : null}

                            <Tooltip title="Добавить расшифровку значений атрибута">
                                <PlusOutlined className="dynamic-delete-button"
                                              style={{margin: 0, position: "absolute", bottom: 0, right: 0}}
                                              onClick={() =>
                                              {
                                                  setFieldValue(`${groupKeyId}.attributes.${attrKeyId}.options.${countOption}`, {
                                                      value: "",
                                                      label: ""
                                                  })
                                                  setCountOption(prev => prev + 1)
                                              }}
                                />
                            </Tooltip>
                        </div>
                    </div>
                </li>
            )
        },
    ),
)
